import { useEffect, useState } from "react";
import { ApiError, getRolePreview } from "../../shared/api/client";
import { productionGateway } from "./production-gateway";
import { COUNTER_POLL_TIMEOUT_MS, counterPollRecoveryDelay, counterPollTimeoutReason } from "./counter-poll-timeout";
import { counterPresentation, machineSpeedPresentation, type LiveCounter } from "./live-counter";
import { machineStatePresentation } from "./machine-state";
import { plcCounterPresentation } from "./plc-counter-presentation";
import {
  reduceStableLiveTelemetry,
  stableTelemetryPresentation,
  type StableLiveTelemetry,
} from "./live-telemetry-state";

import { readTelemetryDisplay, saveTelemetryDisplay } from "./telemetry-display-cache";
import { setTabletDiagnosticCounter } from "./tablet-diagnostics";
import { completedCasesPresentation } from "./operator-completed-cases";

type Reading = { key: string; counter: LiveCounter | null; unavailable: boolean; sessionRequired: boolean; failureCode: string | null };

export function useLiveMachineCounter(runId: string | null, terminalCode: string) {
  const key = `${terminalCode}:${runId ?? ""}`;
  const [reading, setReading] = useState<Reading | null>(null);
  const [stableTelemetry, setStableTelemetry] = useState<{ key: string; value: StableLiveTelemetry | null } | null>(null);
  const [cacheFailed, setCacheFailed] = useState(false);
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    if (!runId) return;
    let disposed = false;
    const preview = Boolean(getRolePreview());
    let stable = readTelemetryDisplay(runId, terminalCode, preview);
    setStableTelemetry({ key, value: stable });
    setReading({ key, counter: null, unavailable: false, sessionRequired: false, failureCode: null });
    setCacheFailed(false);
    let timer: ReturnType<typeof setTimeout>;
    let activeRequest: AbortController | null = null;
    let pending = false;
    let interval = 1000;
    let consecutiveFailures = 0;
    let immediateRecoveryRequested = false;
    const read = async () => {
      if (disposed || pending) return;
      if (document.visibilityState === "hidden") { timer = setTimeout(() => void read(), 1000); return; }
      pending = true;
      const startedAt = performance.now();
      let failed = false;
      activeRequest = new AbortController();
      const timeout = setTimeout(() => activeRequest?.abort(counterPollTimeoutReason), COUNTER_POLL_TIMEOUT_MS);
      try {
        const counter = await productionGateway.liveCounter(runId, terminalCode, activeRequest.signal);
        if (!disposed) {
          setReading({ key, counter, unavailable: false, sessionRequired: false, failureCode: null });
          stable = reduceStableLiveTelemetry(stable, runId, terminalCode, counter, Date.now());
          setStableTelemetry({ key, value: stable });
          if (stable) setCacheFailed(!saveTelemetryDisplay(stable, preview));
          interval = counter.configured ? 1000 : 15000;
          consecutiveFailures = 0;
        }
      } catch (error) {
        failed = true;
        if (!disposed) {
          setReading((previous) => ({
            key, counter: previous?.key === key ? previous.counter : null, unavailable: true,
            failureCode: error instanceof ApiError ? error.code : "CLIENT_REQUEST_FAILURE",
            sessionRequired: error instanceof ApiError && (["HTTP_401", "HTTP_403"].includes(error.code) || error.code.startsWith("TERMINAL_")),
          }));
          consecutiveFailures += 1;
          interval = counterPollRecoveryDelay(consecutiveFailures);
        }
      } finally {
        clearTimeout(timeout);
        pending = false;
        // Keep a one-second start cadence instead of adding another second to
        // every successful response; failed reads retain their recovery backoff.
        if (!disposed) {
          const delay = immediateRecoveryRequested ? 0 : failed ? interval : Math.max(100, interval - (performance.now() - startedAt));
          immediateRecoveryRequested = false;
          timer = setTimeout(() => void read(), delay);
        }
      }
    };
    const wake = () => { clearTimeout(timer); if (!pending) void read(); };
    const recoverOnline = () => {
      clearTimeout(timer);
      if (pending) {
        immediateRecoveryRequested = true;
        activeRequest?.abort();
      } else void read();
    };
    void read();
    const clock = setInterval(() => setNow(Date.now()), 1000);
    document.addEventListener("visibilitychange", wake);
    window.addEventListener("online", recoverOnline);
    return () => {
      disposed = true;
      clearTimeout(timer);
      clearInterval(clock);
      activeRequest?.abort();
      document.removeEventListener("visibilitychange", wake);
      window.removeEventListener("online", recoverOnline);
    };
  }, [key, runId, terminalCode]);
  const current = reading?.key === key && runId ? reading : null;
  const status = counterPresentation(current?.counter ?? null, current?.unavailable ?? false, now);
  const speed = machineSpeedPresentation(current?.counter ?? null, current?.unavailable ?? false, now);
  const machine = machineStatePresentation(current?.counter ?? null, current?.unavailable ?? false, now);
  const plcCounters = plcCounterPresentation(current?.counter ?? null, current?.unavailable ?? false, now);
  const stable = stableTelemetry?.key === key ? stableTelemetry.value : null;
  const currentCounter = current?.counter ?? null;
  useEffect(() => {
    setTabletDiagnosticCounter(currentCounter);
    return () => setTabletDiagnosticCounter(null);
  }, [currentCounter]);
  const telemetry = stableTelemetryPresentation(stable, currentCounter, current?.unavailable ?? false, now, status.label);
  const completedCases = completedCasesPresentation({ current: currentCounter,
    lastRaw: stable?.completedCasesCounter ?? null, lastRun: stable?.completedCaseRunCounter ?? null,
    unavailable: current?.unavailable ?? false, failureCode: current?.failureCode, now });
  return {
    counter: currentCounter,
    status,
    speed,
    machine,
    plcCounters,
    completedCases,
    sessionRequired: current?.sessionRequired ?? false,
    telemetry: { ...telemetry, countLabel: `${telemetry.countLabel}${cacheFailed ? " · Cihaz önbelleği kullanılamıyor; yenilemede son ölçüm korunamayabilir" : ""}` },
  };
}
